import type { NavigationBreadcrumbsConfigItem } from '@shared/ui/menus'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { getDisciplineName } from '../discipline-general-info'
import { useDisciplineStore } from '../store'

interface LessonDetailHeaderOptions {
  /** The last crumb: the lesson itself. */
  title: string
}

interface LessonDetailHeaderReturn {
  config: ComputedRef<NavigationBreadcrumbsConfigItem[]>
}

/** Customer › Kid › Discipline › Lesson (› Connection log). The discipline crumb leads back to its Lessons tab, where
 *  lessons live; the open page's crumb is disabled. */
export const useLessonDetailHeader = ({ title }: LessonDetailHeaderOptions): LessonDetailHeaderReturn => {
  const route = useRoute()
  const disciplineId = route.params.id as string
  const customerStore = useCustomerStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()

  const lessonsUrl = resolveUrl(AppRoute.DisciplinesLessons, { id: disciplineId })
  const lessonParams = { id: disciplineId, lessonId: route.params.lessonId as string }
  const lessonUrl = route.params.lessonId ? resolveUrl(AppRoute.LessonsEdit, lessonParams) : null

  const config = computed<NavigationBreadcrumbsConfigItem[]>(() => {
    const customer = customerStore.getCustomer
    const discipline = disciplineStore.getDiscipline
    const kid = customer?.kids.find(k => k.id === discipline?.kid?.id) ?? null

    const items: NavigationBreadcrumbsConfigItem[] = [
      {
        id: 'customers',
        text: 'Customers',
        to: AppRoute.Customers,
        disabled: false,
      },
    ]

    if (customer) {
      items.push({
        id: 'customer-detail',
        text: `Customer / ${customer.id}`,
        to: resolveUrl(AppRoute.CustomersShow, { id: customer.id }) ?? '',
        disabled: false,
      })
    }

    if (kid) {
      items.push({
        id: 'kids-detail',
        text: `Kids / ${kid.name}`,
        to: resolveUrl(AppRoute.KidsShow, { id: kid.id }) ?? '',
        disabled: false,
      })
    }

    if (discipline) {
      items.push({
        id: 'discipline-detail',
        text: getDisciplineName(discipline.code, listsStore.getLists?.discipline.code),
        to: lessonsUrl,
        disabled: false,
      })
    }

    const isConnectionLog = !!lessonUrl && route.path === resolveUrl(AppRoute.LessonsConnectionLog, lessonParams)

    items.push({
      id: 'lesson-detail',
      text: title,
      to: isConnectionLog ? lessonUrl : null,
      disabled: !isConnectionLog,
    })

    if (isConnectionLog) {
      items.push({
        id: 'lesson-connection-log',
        text: 'Connection log',
        to: null,
        disabled: true,
      })
    }

    return items
  })

  return {
    config,
  }
}
